/**
 * InvoiceSettings - Rechnung & Firmendaten
 * Extrahiert aus SettingsPage.js für bessere Wartbarkeit
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FileText, Save } from 'lucide-react';
import axios from 'axios';
import { PhoneInput } from '@/components/ui/phone-input';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceSettings = () => {
  const [taxSettings, setTaxSettings] = useState({
    company_name: '',
    company_street: '',
    company_postal_code: '',
    company_city: '',
    company_phone: '',
    company_website: '',
    company_email: '',
    pdf_primary_color: '#10b981',
    footer_text: '',
    pdf_greeting_text: '',
    pdf_closing_text: '',
    payment_terms_days: 30,
    bank_iban: '',
    bank_account_holder: '',
    bank_name: '',
    bank_bic: '',
    payment_notes: '',
    enable_card_on_site: true,
    logo_url: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoVersion, setLogoVersion] = useState(() => Date.now());
  const [loading, setLoading] = useState(false);

  const fetchTaxSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoice-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setTaxSettings(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching tax settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchTaxSettings();
  }, [fetchTaxSettings]);

  const handleUploadLogo = async () => {
    if (!logoFile) {
      toast.error('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('logo', logoFile);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invoice-settings/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Logo hochgeladen');
      setLogoVersion(Date.now());
      fetchTaxSettings();
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Fehler beim Hochladen');
    }
  };

  const handleSaveTaxSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        company_name: taxSettings.company_name,
        company_street: taxSettings.company_street,
        company_postal_code: taxSettings.company_postal_code,
        company_city: taxSettings.company_city,
        company_phone: taxSettings.company_phone,
        company_website: taxSettings.company_website,
        company_email: taxSettings.company_email,
        pdf_primary_color: taxSettings.pdf_primary_color,
        footer_text: taxSettings.footer_text,
        pdf_greeting_text: taxSettings.pdf_greeting_text,
        pdf_closing_text: taxSettings.pdf_closing_text,
        payment_terms_days: taxSettings.payment_terms_days,
        bank_iban: taxSettings.bank_iban,
        bank_account_holder: taxSettings.bank_account_holder,
        bank_name: taxSettings.bank_name,
        bank_bic: taxSettings.bank_bic,
        payment_notes: taxSettings.payment_notes,
        enable_card_on_site: taxSettings.enable_card_on_site !== false,
      };

      await axios.put(`${API}/invoice-settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rechnungs-Firmendaten gespeichert');
    } catch (error) {
      console.error('Error saving tax settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText size={24} />
              Rechnung & Firmendaten
            </CardTitle>
            <CardDescription>
              Firmendaten, Kontaktinformationen, Zahlungsinfos und PDF-Design für Rechnungen konfigurieren
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo Upload */}
          <div className="space-y-2">
            <Label>Rechnungs-Logo (PNG/JPG)</Label>
            <div className="flex items-center gap-3">
              <Input
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setLogoFile(file);
                }}
              />
              <Button type="button" variant="outline" onClick={handleUploadLogo}>
                Logo hochladen
              </Button>
            </div>
            {taxSettings.logo_url && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Aktuelles Logo-Vorschau:</p>
                <img
                  src={`${API}/invoice-settings/logo?v=${logoVersion}`}
                  alt="Rechnungslogo"
                  className="h-16 object-contain border rounded bg-white p-1"
                />
              </div>
            )}
            <p className="text-xs text-gray-500">
              Wird oben links auf der Rechnung angezeigt.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Firmenname</Label>
            <Input
              value={taxSettings.company_name || ''}
              onChange={(e) => setTaxSettings({...taxSettings, company_name: e.target.value})}
              placeholder="StrongFit EMS Studio"
            />
          </div>
          <div className="space-y-2">
            <Label>Strasse</Label>
            <Input
              value={taxSettings.company_street || ''}
              onChange={(e) => setTaxSettings({...taxSettings, company_street: e.target.value})}
              placeholder="Musterstrasse 1"
            />
          </div>
          <div className="space-y-2">
            <Label>PLZ</Label>
            <Input
              value={taxSettings.company_postal_code || ''}
              onChange={(e) => setTaxSettings({...taxSettings, company_postal_code: e.target.value})}
              placeholder="8000"
            />
          </div>
          <div className="space-y-2">
            <Label>Ort</Label>
            <Input
              value={taxSettings.company_city || ''}
              onChange={(e) => setTaxSettings({...taxSettings, company_city: e.target.value})}
              placeholder="Zürich"
            />
          </div>
          <div className="space-y-2">
            <Label>Telefon</Label>
            <PhoneInput
              value={taxSettings.company_phone || ''}
              onChange={(e) => setTaxSettings({...taxSettings, company_phone: e.target.value})}
              data-testid="invoice-company-phone"
            />
          </div>
          <div className="space-y-2">
            <Label>Website</Label>
            <Input
              value={taxSettings.company_website || ''}
              onChange={(e) => setTaxSettings({...taxSettings, company_website: e.target.value})}
              placeholder="https://www.strongfit.ch"
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              value={taxSettings.company_email || ''}
              onChange={(e) => setTaxSettings({...taxSettings, company_email: e.target.value})}
              placeholder="info@strongfit.ch"
            />
          </div>

        </div>

        <div className="p-4 rounded-lg border bg-blue-50" data-testid="invoice-settings-source-of-truth-note">
          <p className="text-sm text-blue-900 font-medium">Zentrale Logik</p>
          <p className="text-xs text-blue-800 mt-1">
            MWST wird zentral unter
            {' '}<a className="underline" href="/settings/advanced?tab=tax">Steuern (MWST)</a>{' '}
            gepflegt.
            E-Mail-Vorlagen für Rechnungen werden zentral unter
            {' '}<a className="underline" href="/email/templates">E-Mail Templates</a>{' '}
            verwaltet und beim Rechnungsversand verwendet.
          </p>
        </div>

        {/* PDF-Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Primärfarbe für Rechnung (Hex)</Label>
            <Input
              value={taxSettings.pdf_primary_color || ''}
              onChange={(e) => setTaxSettings({...taxSettings, pdf_primary_color: e.target.value})}
              placeholder="#10b981"
            />
            <p className="text-xs text-gray-500">
              Wird für Tabellenkopf & Gesamtbetrag-Linie im PDF verwendet.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Footer-Text auf der Rechnung</Label>
            <Input
              value={taxSettings.footer_text || ''}
              onChange={(e) => setTaxSettings({...taxSettings, footer_text: e.target.value})}
              placeholder="Vielen Dank für Ihr Vertrauen."
            />
          </div>
        </div>

        {/* Zahlungsinformationen */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50 md:col-span-2" data-testid="card-on-site-setting">
            <div>
              <Label className="font-medium">💳 Kartenzahlung vor Ort anbieten</Label>
              <p className="text-xs text-gray-500 mt-1">
                Zeigt im „Verkaufen & Check-in"-Dialog die Zahlungsart „Karte (vor Ort)" — für Zahlungen am Terminal im Studio. Wird wie Bar sofort als bezahlt verbucht.
              </p>
            </div>
            <Switch
              checked={taxSettings.enable_card_on_site !== false}
              onCheckedChange={(checked) => setTaxSettings({...taxSettings, enable_card_on_site: checked})}
              data-testid="card-on-site-toggle"
            />
          </div>
          <div className="space-y-2">
            <Label>Zahlungsziel (Tage)</Label>
            <Input
              type="number"
              min="0"
              max="180"
              value={taxSettings.payment_terms_days ?? 30}
              onChange={(e) => setTaxSettings({...taxSettings, payment_terms_days: parseInt(e.target.value || '0', 10)})}
            />
          </div>
          <div className="space-y-2">
            <Label>IBAN</Label>
            <Input
              value={taxSettings.bank_iban || ''}
              onChange={(e) => setTaxSettings({...taxSettings, bank_iban: e.target.value})}
              placeholder="CHxx xxxx xxxx xxxx xxxx x"
            />
          </div>
          <div className="space-y-2">
            <Label>Kontoinhaber</Label>
            <Input
              value={taxSettings.bank_account_holder || ''}
              onChange={(e) => setTaxSettings({...taxSettings, bank_account_holder: e.target.value})}
              placeholder="StrongFit GmbH"
            />
          </div>
          <div className="space-y-2">
            <Label>Bankname</Label>
            <Input
              value={taxSettings.bank_name || ''}
              onChange={(e) => setTaxSettings({...taxSettings, bank_name: e.target.value})}
              placeholder="Meine Bank AG"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSaveTaxSettings} disabled={loading}>
            <Save className="mr-2 h-4 w-4" />
            Firmendaten speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvoiceSettings;
